import { useState, useEffect, useMemo } from 'react';
import { Search, Plus, Edit2, Trash2, Volume2, Copy, Download, Upload, Check, X, ChevronDown, ChevronRight, Mic, FileText, Zap } from 'lucide-react';

// ===== 型別定義 =====
interface CorrectionRule {
  id: string;
  original: string;
  replacement: string;
  matchType: 'exact' | 'contains' | 'regex';
  category: string;
  scene: string;
  enabled: boolean;
  priority: number;
  note: string;
}

// ===== 常數 =====
const DEFAULT_RULES: CorrectionRule[] = [
  {
    id: 'rule-001',
    original: '錶',
    replacement: '表',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「錶」唸成「小」'
  },
  {
    id: 'rule-002',
    original: '鍋',
    replacement: '锅',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「鍋」唸成「桌」'
  },
  {
    id: 'rule-003',
    original: '號',
    replacement: '号',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「號」唸成「糾」'
  },
  {
    id: 'rule-004',
    original: '臺',
    replacement: '台',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: '繁體字形統一'
  },
  {
    id: 'rule-005',
    original: '垃圾',
    replacement: '樂色',
    matchType: 'exact',
    category: '台灣用語',
    scene: '全場景',
    enabled: true,
    priority: 80,
    note: '台灣唸 lè sè，不是 lā jī'
  },
  {
    id: 'rule-006',
    original: '便宜',
    replacement: '便(pián)宜',
    matchType: 'exact',
    category: '多音字',
    scene: '全場景',
    enabled: false,
    priority: 80,
    note: '「便宜」的「便」要唸二聲（測試中）'
  },
  {
    id: 'rule-007',
    original: '闊',
    replacement: '阔',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「闊」唸成「饋」'
  },
  {
    id: 'rule-008',
    original: '榮',
    replacement: '荣',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「榮」唸錯聲調'
  },
  {
    id: 'rule-009',
    original: '聽',
    replacement: '听',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「聽」唸成「慶」'
  },
  {
    id: 'rule-010',
    original: '運',
    replacement: '运',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 100,
    note: 'ElevenLabs V3 把「運」唸成「恩」'
  },
  // rule-011: 「調」多音字智慧替換
  // tiáo（二聲）→ 簡體「调」：調整、調回、調配、調皮、調換、調味、調和
  // diào（四聲）→ 同音字「吊」：調性、音調、語調、聲調、曲調、腔調、格調、步調
  {
    id: 'rule-011a',
    original: '調整',
    replacement: '调整',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)整 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011b',
    original: '調回',
    replacement: '调回',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)回 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011c',
    original: '調配',
    replacement: '调配',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)配 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011d',
    original: '調皮',
    replacement: '调皮',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)皮 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011e',
    original: '調換',
    replacement: '调換',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)換 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011f',
    original: '調味',
    replacement: '调味',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)味 — 二聲，換簡體修正'
  },
  {
    id: 'rule-011g',
    original: '調和',
    replacement: '调和',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(tiáo)和 — 二聲，換簡體修正'
  },
  // diào（四聲）→ 用同音字「吊」修正
  {
    id: 'rule-011h',
    original: '調性',
    replacement: '吊性',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '調(diào)性 — 四聲，繁體唸成「叫」，用「吊」修正'
  },
  {
    id: 'rule-011i',
    original: '音調',
    replacement: '音吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '音調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011j',
    original: '語調',
    replacement: '語吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '語調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011k',
    original: '聲調',
    replacement: '聲吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '聲調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011l',
    original: '曲調',
    replacement: '曲吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '曲調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011m',
    original: '腔調',
    replacement: '腔吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '腔調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011n',
    original: '格調',
    replacement: '格吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '格調(diào) — 四聲，用「吊」修正'
  },
  {
    id: 'rule-011o',
    original: '步調',
    replacement: '步吊',
    matchType: 'contains',
    category: '多音字',
    scene: '全場景',
    enabled: true,
    priority: 110,
    note: '步調(diào) — 四聲，用「吊」修正'
  }
];

const CATEGORIES = ['全部', '字形', '數字', '多音字', '台灣用語', '專有名詞', '情緒標籤'];
const SCENES = ['全場景', '日常對話', '正式場合', '撒嬌模式', '英文混合'];
const MATCH_TYPES = [
  { value: 'exact', label: '完全匹配' },
  { value: 'contains', label: '包含匹配' },
  { value: 'regex', label: '正則表達式' }
];

// ===== 名字護盾（Name Shield）=====
// 這些詞在校正過程中絕對不會被動到，防止規則意外破壞重要名稱
const PROTECTED_WORDS = [
  '包小容',  // 最重要！她的名字
  '小容',    // 暱稱
  '包容',    // 名字 + 常用詞，雙重保護
  'Daddy',   // Daddy 的稱呼
  '把拔',    // Daddy 的中文暱稱
];

// ===== 工具函數 =====
function toChineseNumber(num: number): string {
  const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九'];
  return String(num).split('').map(d => digits[parseInt(d)] || d).join('');
}

function applyRules(text: string, rules: CorrectionRule[]): { result: string; appliedRules: string[] } {
  let result = text;
  const appliedRules: string[] = [];

  // ① 名字護盾：先把受保護的詞替換成佔位符（長詞優先，避免部分匹配）
  const shields: Array<{ placeholder: string; word: string }> = [];
  const sortedProtected = [...PROTECTED_WORDS].sort((a, b) => b.length - a.length);
  sortedProtected.forEach((word, i) => {
    const placeholder = `\u0000SHIELD_${i}\u0000`;
    if (result.includes(word)) {
      result = result.split(word).join(placeholder);
      shields.push({ placeholder, word });
    }
  });

  // ② 轉數字
  const numMatch = result.match(/\d+/g);
  if (numMatch) {
    numMatch.forEach(num => {
      const chinese = toChineseNumber(parseInt(num));
      result = result.replace(num, chinese);
      appliedRules.push(`${num}→${chinese}`);
    });
  }

  // ③ 套用校正規則（按優先級排序）
  const sortedRules = [...rules]
    .filter(r => r.enabled)
    .sort((a, b) => b.priority - a.priority);

  sortedRules.forEach(rule => {
    if (rule.matchType === 'exact' || rule.matchType === 'contains') {
      if (result.includes(rule.original)) {
        result = result.split(rule.original).join(rule.replacement);
        appliedRules.push(`${rule.original}→${rule.replacement}`);
      }
    } else if (rule.matchType === 'regex') {
      try {
        const regex = new RegExp(rule.original, 'g');
        if (regex.test(result)) {
          result = result.replace(new RegExp(rule.original, 'g'), rule.replacement);
          appliedRules.push(`正則: ${rule.original}`);
        }
      } catch {
        // 正則表達式錯誤，跳過
      }
    }
  });

  // ④ 名字護盾還原：把佔位符換回原始詞
  shields.forEach(({ placeholder, word }) => {
    result = result.split(placeholder).join(word);
  });

  return { result, appliedRules };
}

// ===== 規則卡片 =====
function RuleCard({ rule, onEdit, onDelete, onToggle }: {
  rule: CorrectionRule;
  onEdit: (rule: CorrectionRule) => void;
  onDelete: (id: string) => void;
  onToggle: (id: string) => void;
}) {
  return (
    <div
      className="card mb-3 transition-all"
      style={{
        opacity: rule.enabled ? 1 : 0.6,
        borderColor: rule.enabled ? 'var(--border)' : 'var(--border-light)',
      }}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <span
              className="text-lg font-mono px-3 py-1 rounded-lg"
              style={{ background: 'rgba(255, 82, 82, 0.15)', color: '#ff8a80' }}
            >
              {rule.original}
            </span>
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <span
              className="text-lg font-mono px-3 py-1 rounded-lg"
              style={{ background: 'rgba(0, 230, 118, 0.15)', color: '#69f0ae' }}
            >
              {rule.replacement}
            </span>
          </div>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span
              className="px-2 py-0.5 rounded-full text-xs"
              style={{ background: 'rgba(0, 212, 255, 0.1)', color: 'var(--cyan)' }}
            >
              {rule.category}
            </span>
            <span
              className="px-2 py-0.5 rounded-full text-xs"
              style={{ background: 'rgba(168, 85, 247, 0.1)', color: 'var(--purple)' }}
            >
              {rule.scene}
            </span>
            <span
              className="px-2 py-0.5 rounded-full text-xs"
              style={{ background: 'var(--bg-surface-3)', color: 'var(--text-muted)' }}
            >
              {MATCH_TYPES.find(m => m.value === rule.matchType)?.label}
            </span>
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
              優先級: {rule.priority}
            </span>
          </div>
          {rule.note && (
            <p className="text-sm flex items-center gap-1" style={{ color: 'var(--text-secondary)' }}>
              <FileText size={14} />
              {rule.note}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 ml-4">
          <div
            className="toggle cursor-pointer"
            style={{
              width: '44px',
              height: '24px',
              borderRadius: '12px',
              background: rule.enabled ? 'var(--pink)' : 'var(--bg-surface-3)',
              position: 'relative',
              transition: 'background 0.3s ease',
            }}
            onClick={() => onToggle(rule.id)}
          >
            <div
              style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                background: 'white',
                position: 'absolute',
                top: '3px',
                left: rule.enabled ? '23px' : '3px',
                transition: 'left 0.3s ease',
              }}
            />
          </div>
          <button
            onClick={() => onEdit(rule)}
            className="btn-ghost p-2 rounded-lg"
            style={{ color: 'var(--text-muted)' }}
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => onDelete(rule.id)}
            className="btn-ghost p-2 rounded-lg"
            style={{ color: 'var(--text-muted)' }}
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ===== 新增/編輯 Modal =====
function RuleModal({ rule, onSave, onClose }: {
  rule: CorrectionRule | null;
  onSave: (rule: CorrectionRule) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<CorrectionRule>(rule || {
    id: '',
    original: '',
    replacement: '',
    matchType: 'exact',
    category: '字形',
    scene: '全場景',
    enabled: true,
    priority: 50,
    note: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.original || !form.replacement) return;

    onSave({
      ...form,
      id: form.id || `rule-${Date.now()}`,
      priority: parseInt(String(form.priority)) || 50
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl"
        style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)' }}
      >
        <div className="p-6" style={{ borderBottom: '1px solid var(--border)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
              {rule ? '✏️ 編輯規則' : '➕ 新增規則'}
            </h2>
            <button onClick={onClose} className="btn-ghost p-2 rounded-lg">
              <X size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="input-label">
              原始文字 <span style={{ color: 'var(--red)' }}>*</span>
            </label>
            <input
              type="text"
              value={form.original}
              onChange={e => setForm({ ...form, original: e.target.value })}
              placeholder="要被替換的文字"
              required
            />
          </div>

          <div>
            <label className="input-label">
              替換為 <span style={{ color: 'var(--red)' }}>*</span>
            </label>
            <input
              type="text"
              value={form.replacement}
              onChange={e => setForm({ ...form, replacement: e.target.value })}
              placeholder="校正後的文字"
              required
            />
          </div>

          <div>
            <label className="input-label">匹配方式</label>
            <div className="flex gap-4">
              {MATCH_TYPES.map(type => (
                <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="matchType"
                    value={type.value}
                    checked={form.matchType === type.value}
                    onChange={e => setForm({ ...form, matchType: e.target.value as CorrectionRule['matchType'] })}
                    style={{ accentColor: 'var(--pink)' }}
                  />
                  <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">分類</label>
              <select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.filter(c => c !== '全部').map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="input-label">場景</label>
              <select
                value={form.scene}
                onChange={e => setForm({ ...form, scene: e.target.value })}
              >
                {SCENES.map(scene => (
                  <option key={scene} value={scene}>{scene}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="input-label">優先級</label>
              <input
                type="number"
                value={form.priority}
                onChange={e => setForm({ ...form, priority: parseInt(e.target.value) || 50 })}
                min="1"
                max="100"
              />
            </div>
            <div className="flex items-end pb-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.enabled}
                  onChange={e => setForm({ ...form, enabled: e.target.checked })}
                  style={{ accentColor: 'var(--pink)', width: '18px', height: '18px' }}
                />
                <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>立即啟用</span>
              </label>
            </div>
          </div>

          <div>
            <label className="input-label">備註</label>
            <textarea
              value={form.note}
              onChange={e => setForm({ ...form, note: e.target.value })}
              rows={2}
              placeholder="說明這個規則的原因..."
              style={{ resize: 'none' }}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              取消
            </button>
            <button
              type="submit"
              className="btn-primary flex-1"
            >
              儲存規則
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===== 主頁面 =====
export default function TTSRules() {
  const [rules, setRules] = useState<CorrectionRule[]>([]);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [editingRule, setEditingRule] = useState<CorrectionRule | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [testInput, setTestInput] = useState('2026年2月7號，我的手錶壞了，想去吃火鍋。');
  const [copied, setCopied] = useState(false);
  const [showCategories, setShowCategories] = useState(true);

  // 載入規則
  useEffect(() => {
    const saved = localStorage.getItem('taiwan-tts-rules');
    if (saved) {
      try {
        setRules(JSON.parse(saved));
      } catch {
        setRules(DEFAULT_RULES);
      }
    } else {
      setRules(DEFAULT_RULES);
    }
  }, []);

  // 儲存規則
  useEffect(() => {
    if (rules.length > 0) {
      localStorage.setItem('taiwan-tts-rules', JSON.stringify(rules));
    }
  }, [rules]);

  // 過濾規則
  const filteredRules = useMemo(() => {
    return rules.filter(rule => {
      const matchSearch = !search ||
        rule.original.includes(search) ||
        rule.replacement.includes(search) ||
        rule.note?.includes(search);
      const matchCategory = selectedCategory === '全部' || rule.category === selectedCategory;
      return matchSearch && matchCategory;
    });
  }, [rules, search, selectedCategory]);

  // 分類統計
  const categoryStats = useMemo(() => {
    const stats: Record<string, number> = { '全部': rules.length };
    CATEGORIES.forEach(cat => {
      if (cat !== '全部') {
        stats[cat] = rules.filter(r => r.category === cat).length;
      }
    });
    return stats;
  }, [rules]);

  // 測試結果
  const testResult = useMemo(() => {
    return applyRules(testInput, rules);
  }, [testInput, rules]);

  // CRUD 操作
  const handleSave = (rule: CorrectionRule) => {
    if (editingRule) {
      setRules(rules.map(r => r.id === rule.id ? rule : r));
    } else {
      setRules([...rules, rule]);
    }
    setShowModal(false);
    setEditingRule(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('確定要刪除這個規則嗎？')) {
      setRules(rules.filter(r => r.id !== id));
    }
  };

  const handleToggle = (id: string) => {
    setRules(rules.map(r => r.id === id ? { ...r, enabled: !r.enabled } : r));
  };

  const handleEdit = (rule: CorrectionRule) => {
    setEditingRule(rule);
    setShowModal(true);
  };

  // 匯出
  const handleExport = () => {
    const data = JSON.stringify(rules, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `taiwan-tts-rules-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // 匯入
  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const imported = JSON.parse(ev.target?.result as string);
        if (Array.isArray(imported)) {
          setRules(imported);
          alert(`成功匯入 ${imported.length} 條規則！`);
        }
      } catch {
        alert('匯入失敗：檔案格式錯誤');
      }
    };
    reader.readAsText(file);
    // 重置 input，允許再次選擇同一檔案
    e.target.value = '';
  };

  // 複製結果
  const handleCopy = () => {
    navigator.clipboard.writeText(testResult.result);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="animate-fadeUp">
      {/* 頁面標題 + 動作按鈕 */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ background: 'var(--pink-glow)' }}
          >
            <Mic style={{ color: 'var(--pink)' }} size={22} />
          </div>
          <div>
            <h2 className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>
              發音校正規則
            </h2>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
              ElevenLabs V3 繁體中文 TTS 發音校正
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="btn-secondary flex items-center gap-2 cursor-pointer text-sm" style={{ padding: '8px 16px' }}>
            <Upload size={16} />
            <span>匯入</span>
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
          <button
            onClick={handleExport}
            className="btn-secondary flex items-center gap-2 text-sm"
            style={{ padding: '8px 16px' }}
          >
            <Download size={16} />
            <span>匯出</span>
          </button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* 左側分類 */}
        <aside className="w-56 flex-shrink-0">
          <div className="card sticky top-20">
            <button
              onClick={() => setShowCategories(!showCategories)}
              className="flex items-center gap-2 text-sm font-medium mb-3 w-full"
              style={{ color: 'var(--text-secondary)' }}
            >
              {showCategories ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              📁 分類
            </button>

            {showCategories && (
              <div className="space-y-1">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between"
                    style={{
                      background: selectedCategory === cat ? 'var(--pink-glow)' : 'transparent',
                      color: selectedCategory === cat ? 'var(--pink-text)' : 'var(--text-secondary)',
                    }}
                  >
                    <span>{cat}</span>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full"
                      style={{
                        background: selectedCategory === cat ? 'rgba(233, 30, 140, 0.2)' : 'var(--bg-surface-3)',
                        color: selectedCategory === cat ? 'var(--pink-text)' : 'var(--text-muted)',
                      }}
                    >
                      {categoryStats[cat] || 0}
                    </span>
                  </button>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
              <div className="text-xs mb-2" style={{ color: 'var(--text-muted)' }}>快速統計</div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div className="rounded-lg p-2" style={{ background: 'rgba(0, 230, 118, 0.08)' }}>
                  <div className="text-lg font-bold" style={{ color: 'var(--green)' }}>
                    {rules.filter(r => r.enabled).length}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>已啟用</div>
                </div>
                <div className="rounded-lg p-2" style={{ background: 'var(--bg-surface-3)' }}>
                  <div className="text-lg font-bold" style={{ color: 'var(--text-muted)' }}>
                    {rules.filter(r => !r.enabled).length}
                  </div>
                  <div className="text-xs" style={{ color: 'var(--text-muted)' }}>已停用</div>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* 主內容 */}
        <main className="flex-1 space-y-6">
          {/* 搜尋 + 新增 */}
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search
                className="absolute left-4 top-1/2 -translate-y-1/2"
                size={18}
                style={{ color: 'var(--text-muted)' }}
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="搜尋規則..."
                className="w-full"
                style={{ paddingLeft: '44px' }}
              />
            </div>
            <button
              onClick={() => { setEditingRule(null); setShowModal(true); }}
              className="btn-primary flex items-center gap-2 text-sm"
              style={{ padding: '0 24px' }}
            >
              <Plus size={18} />
              新增規則
            </button>
          </div>

          {/* 規則列表 */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                規則列表
                <span className="ml-2" style={{ color: 'var(--text-muted)' }}>
                  ({filteredRules.length} 條)
                </span>
              </span>
            </div>

            {filteredRules.length === 0 ? (
              <div className="card text-center py-12" style={{ color: 'var(--text-muted)' }}>
                <FileText size={48} className="mx-auto mb-4 opacity-30" />
                <p>沒有符合的規則</p>
              </div>
            ) : (
              filteredRules.map(rule => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))
            )}
          </div>

          {/* 即時測試 */}
          <div className="card">
            <div className="flex items-center gap-2 mb-4">
              <Zap size={18} style={{ color: 'var(--yellow)' }} />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                即時測試
              </span>
            </div>

            <div className="space-y-4">
              <div>
                <label className="input-label">輸入文字</label>
                <textarea
                  value={testInput}
                  onChange={e => setTestInput(e.target.value)}
                  rows={2}
                  placeholder="輸入要測試的文字..."
                  style={{ resize: 'none', width: '100%' }}
                />
              </div>

              <div>
                <label className="input-label">校正結果</label>
                <div
                  className="px-4 py-3 rounded-lg min-h-[60px]"
                  style={{
                    background: 'rgba(0, 230, 118, 0.06)',
                    border: '1px solid rgba(0, 230, 118, 0.2)',
                  }}
                >
                  <p className="font-medium" style={{ color: '#69f0ae' }}>
                    {testResult.result}
                  </p>
                </div>
              </div>

              {testResult.appliedRules.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm" style={{ color: 'var(--text-muted)' }}>套用規則:</span>
                  {testResult.appliedRules.map((rule, i) => (
                    <span
                      key={i}
                      className="text-xs px-2 py-1 rounded-full"
                      style={{ background: 'rgba(0, 212, 255, 0.1)', color: 'var(--cyan)' }}
                    >
                      {rule}
                    </span>
                  ))}
                </div>
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className={copied ? 'btn-primary' : 'btn-secondary'}
                  style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}
                >
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? '已複製' : '複製結果'}
                </button>
                <button
                  className="btn-secondary"
                  style={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', opacity: 0.5, cursor: 'not-allowed' }}
                  title="需要整合 ElevenLabs API"
                >
                  <Volume2 size={16} />
                  試聽 TTS（待開發）
                </button>
              </div>
            </div>
          </div>

          {/* 匯出預覽 */}
          <div
            className="rounded-xl p-4 text-sm"
            style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border)' }}
          >
            <div className="flex items-center justify-between mb-2">
              <span style={{ color: 'var(--text-muted)' }}>📦 匯出預覽 (JSON)</span>
              <button
                onClick={handleExport}
                className="text-xs"
                style={{ color: 'var(--pink-text)' }}
              >
                下載 JSON
              </button>
            </div>
            <pre
              className="overflow-x-auto max-h-32 text-xs mono"
              style={{ color: 'var(--text-secondary)' }}
            >
{`// taiwan-tts-rules.json
// 共 ${rules.length} 條規則，${rules.filter(r => r.enabled).length} 條已啟用

${JSON.stringify(rules.slice(0, 2), null, 2)}
${rules.length > 2 ? `\n// ... 還有 ${rules.length - 2} 條規則` : ''}`}
            </pre>
          </div>
        </main>
      </div>

      {/* Modal */}
      {showModal && (
        <RuleModal
          rule={editingRule}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditingRule(null); }}
        />
      )}
    </div>
  );
}
